import { useEffect, useState, type RefObject } from 'react';

/**
 * Whether a scrolling row runs on past its right edge and has not been scrolled
 * to the end (spec 227 FR-011): the one fact the `overflow-fade` utility draws.
 *
 * Measured on size (a `ResizeObserver` on the element) and on scroll, and once
 * on mount; `data-overflowing` is written onto the element so the utility's
 * `[data-overflowing="true"]` selector can read it with no second render path.
 * The boolean is returned as well for anything that wants to say it in words.
 *
 * One hook for the three tab strips and the chip rows, so "the strip goes on"
 * is said the same way everywhere (SC-005).
 */
export function useOverflowing(ref: RefObject<HTMLElement | null>): boolean {
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => {
      const more =
        element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
      element.dataset.overflowing = more ? 'true' : 'false';
      setOverflowing(more);
    };
    measure();
    // jsdom has no ResizeObserver; the tests that need one stub it.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    observer?.observe(element);
    element.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      element.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [ref]);

  return overflowing;
}
